"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { useAppContext } from "../../context/AppContext";
import { Icon } from "../../icons";
import type { BrandConfig, NavItem } from "../../types";
import { cn } from "../../utils/cn";

import { LanguageSwitcher } from "./LanguageSwitcher";

type LeftSidebarProps = {
  locale: string;
  brand: BrandConfig;
  navItems: NavItem[];
  locales?: readonly string[];
  languageLabels?: Record<string, string>;
  onNavClick?: () => void;
};

export function LeftSidebar({
  locale,
  brand,
  navItems,
  locales,
  languageLabels,
  onNavClick,
}: LeftSidebarProps) {
  const t = useTranslations();
  const { sidebarCollapsed, setSidebarCollapsed } = useAppContext();
  const pathname = usePathname();

  function isActive(href: string) {
    const withoutLocale = pathname.replace(`/${locale}`, "") || "/";
    if (href === "/") return withoutLocale === "/";
    return withoutLocale.startsWith(href);
  }

  return (
    <div data-testid="sidebar" className="flex h-full flex-col bg-sidebar-bg">
      {/* Logo */}
      <div
        className={cn("flex items-center px-5 py-7", {
          "justify-center px-3": sidebarCollapsed,
        })}
      >
        {!sidebarCollapsed ? (
          <Image
            src={brand.logoSrc}
            alt={brand.name}
            width={brand.logoWidth ?? 160}
            height={brand.logoHeight ?? 40}
            className={brand.logoPreStyled ? "" : "brightness-0 invert"}
            style={{ height: "auto" }}
            unoptimized
            priority
          />
        ) : (
          <Image
            src={brand.logoIconSrc}
            alt={brand.name}
            width={40}
            height={40}
            style={{ height: "auto" }}
            unoptimized
            priority
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="shell-scrollbar flex-1 overflow-y-auto px-3 py-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.href}>
              <Link
                href={`/${locale}${item.href === "/" ? "" : item.href}`}
                onClick={onNavClick}
                data-testid="sidebar-nav-item"
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium",
                  "transition-all duration-shell ease-shell",
                  {
                    "bg-sidebar-active-bg text-sidebar-active-text shadow-sm":
                      isActive(item.href),
                    "text-sidebar-text hover:bg-pureWhite/10 hover:text-sidebar-text-hover":
                      !isActive(item.href),
                    "justify-center px-2.5": sidebarCollapsed,
                  },
                )}
                title={sidebarCollapsed ? t(item.labelKey) : undefined}
              >
                <Icon name={item.icon} size={20} />
                {!sidebarCollapsed && (
                  <span className="truncate">{t(item.labelKey)}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="border-t border-pureWhite/10 p-3">
        {locales && locales.length > 1 && languageLabels && (
          <LanguageSwitcher
            locale={locale}
            locales={locales}
            labels={languageLabels}
            collapsed={sidebarCollapsed}
          />
        )}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          data-testid="sidebar-collapse"
          className={cn(
            "hidden w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium",
            "text-sidebar-text transition-all duration-shell ease-shell",
            "hover:bg-pureWhite/10 hover:text-sidebar-text-hover lg:flex",
            { "justify-center px-2.5": sidebarCollapsed },
          )}
        >
          <Icon
            name={sidebarCollapsed ? "chevronRight" : "chevronLeft"}
            size={18}
          />
          {!sidebarCollapsed && <span>{t("nav.collapse")}</span>}
        </button>
      </div>
    </div>
  );
}
